/**
 * SignaturePad — Canvas para dibujar una firma a mano con mouse/touch.
 *
 * Wrapper delgado sobre la librería `signature_pad`. Expone `clear()` y
 * `getDataUrl()` vía ref para que el formulario que lo contiene decida cuándo
 * subir la imagen resultante (solo al guardar, no en cada trazo).
 */

import React, { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import SignaturePadLib from "signature_pad";
import { theme } from "antd";

export interface SignaturePadHandle {
  /** Borra el trazo actual del canvas. */
  clear: () => void;
  /** `null` si el usuario no ha dibujado nada. */
  getDataUrl: () => string | null;
}

interface SignaturePadProps {
  height?: number;
}

const SignaturePad = forwardRef<SignaturePadHandle, SignaturePadProps>(({ height = 160 }, ref) => {
  const { token } = theme.useToken();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const padRef = useRef<SignaturePadLib | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const pad = new SignaturePadLib(canvas, {
      backgroundColor: "rgba(255,255,255,1)",
      penColor: token.colorText,
    });
    padRef.current = pad;

    // El canvas necesita su resolución en px reales; se ajusta al contenedor
    // para que el trazo no se vea borroso ni desalineado del cursor.
    const resize = () => {
      const ratio = Math.max(window.devicePixelRatio || 1, 1);
      const { width } = container.getBoundingClientRect();
      const data = pad.toData();
      canvas.width = width * ratio;
      canvas.height = height * ratio;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      canvas.getContext("2d")?.scale(ratio, ratio);
      pad.clear();
      if (data.length) pad.fromData(data);
    };

    resize();
    window.addEventListener("resize", resize);
    return () => {
      window.removeEventListener("resize", resize);
      pad.off();
      padRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [height]);

  useImperativeHandle(ref, () => ({
    clear: () => padRef.current?.clear(),
    getDataUrl: () => {
      const pad = padRef.current;
      if (!pad || pad.isEmpty()) return null;
      return pad.toDataURL("image/png");
    },
  }));

  return (
    <div ref={containerRef} style={{ width: "100%" }}>
      <canvas
        ref={canvasRef}
        style={{
          display: "block",
          width: "100%",
          height,
          border: `1px dashed ${token.colorBorder}`,
          borderRadius: token.borderRadius,
          background: "#fff",
          touchAction: "none",
          cursor: "crosshair",
        }}
      />
    </div>
  );
});

SignaturePad.displayName = "SignaturePad";

export default SignaturePad;
